<template>
  <b-modal
    id="treefolder-modal"
    size="lg"
    cancel-title="Annulla"
    cancel-variant="outline-secondary"
    centered
    :ok-title="uploading ? 'Caricamento in corso...' : 'Carica'"
    :ok-disabled="!canSubmit"
    :cancel-disabled="uploading"
    :no-close-on-backdrop="uploading"
    :no-close-on-esc="uploading"
    :hide-header-close="uploading"
    title="Seleziona uno o più file ZIP"
    @ok="onOk"
    @show="onShow"
  >
    <div :class="{ 'tree-modal-locked': uploading }">
      <DropZone
        :key="dropZoneKey"
        accept=".zip"
        @drop="getFiles"
      />
    </div>
    <small
      v-if="limitText"
      class="d-block text-muted mt-50"
    >{{ limitText }}</small>
    <small class="d-block text-muted mt-50">
      Ogni archivio .zip viene estratto nella cartella corrente mantenendo cartelle e file.
    </small>
    <b-alert
      variant="warning"
      class="mt-1 mb-0"
      :show="selectionProblem.blocked"
    >
      <div class="alert-body">
        {{ selectionProblem.message }}
        <ul
          v-if="selectionProblem.offenders.length > 0"
          class="mb-0 mt-50"
        >
          <li
            v-for="offender in selectionProblem.offenders"
            :key="offender"
          >
            {{ offender }}
          </li>
        </ul>
      </div>
    </b-alert>
    <div
      v-if="uploading"
      class="mt-1"
    >
      <b-progress
        :value="progress.barValue"
        :max="100"
        :show-progress="progress.determinate && progress.phase === 'uploading'"
        height="1.25rem"
        animated
        striped
      />
      <small class="d-block mt-50">{{ progressText }}</small>
    </div>
    <b-alert
      variant="danger"
      class="mt-1 mb-0"
      :show="Boolean(errorMessage)"
    >
      <div class="alert-body">
        {{ errorMessage }}
      </div>
    </b-alert>
  </b-modal>
</template>

<script>
import {
  BModal, BProgress, BAlert,
} from 'bootstrap-vue'
import folderTreeRepository from '@/api/folderTreeRepository'
import objectsRepository from '@/api/objectsRepository'
import useMyStore from '@/services/useMyStore'
import uploadLimits from '@/helpers/upload-limits'
import importFeedback from '@/helpers/import-feedback'
import apiErrors from '@/helpers/api-errors'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import DropZone from '../DropZone.vue'

export default {
  name: 'UploadFolderTreeModal',
  components: {
    DropZone,
    BModal,
    BProgress,
    BAlert,
  },
  setup() {
    const {
      currentObject,
      currentFolder,
    } = useMyStore()
    return {
      currentObject,
      currentFolder,
    }
  },
  data() {
    return {
      files: null,
      uploading: false,
      progress: uploadLimits.getProgress(),
      errorMessage: '',
      limit: null,
      dropZoneKey: 0,
    }
  },
  computed: {
    filesToUpload() {
      return Array.isArray(this.files) ? this.files : []
    },
    limitText() {
      return this.limit
        ? `Dimensione massima per archivio: ${this.limit.human} (per alberature più grandi usa il caricamento via FTP)`
        : ''
    },
    selectionProblem() {
      const problem = importFeedback.treeSelectionProblem(this.filesToUpload, this.currentFolder)
      if (problem.blocked) {
        return problem
      }
      return uploadLimits.checkSelection(this.filesToUpload, this.limit)
    },
    canSubmit() {
      return !this.uploading && this.filesToUpload.length > 0 && !this.selectionProblem.blocked
    },
    progressText() {
      return this.progress.phase === 'processing'
        ? importFeedback.TREE_PROCESSING_LABEL
        : uploadLimits.progressLabel(this.progress)
    },
    selectionKey() {
      return this.filesToUpload.map(file => `${file.name}:${file.size}`).join('|')
    },
  },
  watch: {
    selectionKey() {
      this.errorMessage = ''
    },
  },
  methods: {
    getFiles(value) {
      this.files = value
    },
    onShow() {
      this.files = null
      this.dropZoneKey += 1
      this.errorMessage = ''
      this.progress = uploadLimits.getProgress()
      this.limit = null
      this.loadLimit()
    },
    async loadLimit() {
      try {
        this.limit = uploadLimits.parseUploadLimit(await objectsRepository.uploadLimit())
      } catch (error) {
        // Degrado silenzioso: senza limite noto non c'è pre-controllo
        this.limit = null
        apiErrors.logApiError(error)
      }
    },
    async onOk(bvModalEvent) {
      // Il modal non deve chiudersi da solo: lo chiudiamo a risposta ricevuta
      bvModalEvent.preventDefault()
      if (this.uploading) {
        return
      }
      await this.upload()
    },
    onUploadProgress(event) {
      this.progress = uploadLimits.getProgress(event)
    },
    async upload() {
      if (this.filesToUpload.length === 0) {
        this.showError('Seleziona almeno un archivio .zip da caricare.')
        return
      }
      if (this.selectionProblem.blocked) {
        this.showError(this.selectionProblem.message)
        return
      }

      this.errorMessage = ''
      this.progress = uploadLimits.getProgress()
      this.uploading = true

      let failure = null
      let response = null
      try {
        const formData = new FormData()
        this.filesToUpload.forEach(file => {
          formData.append('files[]', file)
        })
        formData.append('father_id', this.currentFolder)
        // Nessun timeout client (come per il singolo file): su una linea lenta il caricamento
        // può durare a lungo e la barra mostra che è vivo.
        response = await folderTreeRepository.add(formData, { onUploadProgress: this.onUploadProgress })
        if (!(response && response.data && response.data.code)) {
          failure = response
        }
      } catch (error) {
        failure = error
      } finally {
        this.uploading = false
      }

      if (failure) {
        this.reportFailure(failure)
        return
      }

      const summary = importFeedback.treeUploadSummary(response.data)
      this.$toast({
        component: ToastificationContent,
        props: {
          title: summary.variant === 'success' ? 'Successo!' : 'Attenzione',
          text: summary.message,
          icon: summary.variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant: summary.variant,
        },
      },
      {
        position: 'top-center',
      })
      this.$emit('uploaded')
      this.$bvModal.hide('treefolder-modal')
    },
    reportFailure(failure) {
      apiErrors.logApiError(failure)
      const result = uploadLimits.classifyUploadFailure(failure, {
        limit: this.limit,
        fileCount: this.filesToUpload.length,
      })
      this.showError(importFeedback.treeFailureMessage(result, failure))
    },
    showError(message) {
      this.errorMessage = message
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Errore!',
          text: message,
          icon: 'XIcon',
          variant: 'danger',
        },
      },
      {
        position: 'top-center',
      })
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>

<style>
[dir="ltr"] .modal-header .close {
  margin: -0.5rem -0.6rem -0.5rem auto;
}

.tree-modal-locked {
  pointer-events: none;
  opacity: 0.6;
}
</style>
